import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { SchedulerContact } from './useMeetingSchedulerData';

dayjs.extend(utc);
dayjs.extend(timezone);

// Mirrors the fallback in MeetingDetails/TopHeader.tsx — if utcStartTime/utcEndTime
// are ever missing from the API response, derive a real datetime from the local
// {date, time} fields instead of silently defaulting to "now".
/** Exported so callers comparing a form against its initialData parse times the
 *  same way this hook did when it seeded the form — two parsers would drift. */
export const resolveMeetingTime = (utcValue: string | undefined, local: { date: string; time: string } | undefined, tz: string | undefined) => {
  if (utcValue) return dayjs(utcValue);
  if (local?.date && local?.time) {
    return dayjs.tz(`${local.date}T${local.time}`, tz || 'Asia/Kolkata').local();
  }
  return null;
};

/**
 * The advisor's real IANA zone, not a guess. utcStartTime/utcEndTime below
 * are already computed correctly from the browser's actual local time
 * (dayjs .toISOString() needs no zone argument for that), but the separate
 * `timeZone` field used to be hardcoded to 'Australia/Sydney' regardless of
 * where the advisor actually was — harmless while nothing read it back, but
 * the backend's meeting-reminder push now renders a customer-facing clock
 * time straight from this field, so a wrong value here is a wrong-time bug
 * for anyone outside Sydney, not just stale metadata.
 */
const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export interface MeetingType {
  _id: string;
  templateId: string;
  orgId: string;
  userId: string;
  templateName: string;
  templateType: string;
}

interface FormState {
  _id: string;
  title: string;
  description: string;
  selectedClient: string;
  selectedContacts: string[];
  selectedAdvisors: string[];
  meetingType: string;
  meetingTypeId: string;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  duration: number;
  meetingMedium: string;
  fileNoteTemplateId: string;
  meetingCategory: 'financial' | 'general';
  participants: string;
}

interface FormErrors {
  title: boolean;
  selectedContacts: boolean;
  meetingType: boolean;
  selectedClient: boolean;
  calendlyEvent: boolean;
}

interface UseMeetingSchedulerFormInput {
  open: boolean;
  mode: 'create' | 'edit' | 'upload';
  initialData: any;
  clientId?: string;
  login: any;
  meetingTypes: MeetingType[];
  advisors: any[];
  context: 'client' | 'global';
  schedulingMethod: string;
  selectedCalendlyUrl: string;
  fetchContactsForClient: (id: string) => Promise<SchedulerContact[]>;
  fetchOrgContacts: () => Promise<SchedulerContact[]>;
  resetCalendly: () => void;
}

export const useMeetingSchedulerForm = ({
  open,
  mode,
  initialData,
  clientId,
  login,
  meetingTypes,
  advisors,
  context,
  schedulingMethod,
  selectedCalendlyUrl,
  fetchContactsForClient,
  fetchOrgContacts,
  resetCalendly,
}: UseMeetingSchedulerFormInput) => {
  const navigate = useNavigate();

  const filteredMeetingTypes = useMemo(
    () => meetingTypes.filter((t) => t.templateType === 'file note'),
    [meetingTypes],
  );

  const filteredAdvisors = useMemo(() => {
    const role = login?.data?.role;
    const orgId = login?.data?.orgId;
    if (role === 'global admin') return advisors;
    return advisors.filter((a) => a.orgId === orgId);
  }, [advisors, login]);

  const makeInitialForm = (): FormState => ({
    _id: '',
    title: '',
    description: '',
    selectedClient: clientId || '',
    selectedContacts: [],
    selectedAdvisors: [],
    meetingType: '',
    meetingTypeId: '',
    start: dayjs().second(0).millisecond(0),
    end: dayjs().second(0).millisecond(0).add(30, 'minute'),
    duration: 30,
    meetingMedium: 'PC / Microphone',
    fileNoteTemplateId: '',
    meetingCategory: 'financial',
    participants: '',
  });

  const [form, setForm] = useState<FormState>(makeInitialForm);
  const [errors, setErrors] = useState<FormErrors>({
    title: false, selectedContacts: false, meetingType: false, selectedClient: false, calendlyEvent: false,
  });

  useEffect(() => {
    if (!open) {
      setForm(makeInitialForm());
      setErrors({ title: false, selectedContacts: false, meetingType: false, selectedClient: false, calendlyEvent: false });
      return;
    }
    resetCalendly();
    const targetClientId = mode === 'edit' && initialData?.clientid ? initialData.clientid : clientId;
    const contactsPromise = targetClientId ? fetchContactsForClient(targetClientId) : fetchOrgContacts();
    contactsPromise.then((fetched) => {
      if (mode === 'edit' && initialData) {
        const resolvedStart = resolveMeetingTime(initialData.utcStartTime, initialData.start, initialData.timeZone);
        const resolvedEnd = resolveMeetingTime(initialData.utcEndTime, initialData.end, initialData.timeZone);
        const start = resolvedStart ?? dayjs();
        const end = resolvedEnd ?? start.add(30, 'minute');
        const tpl = filteredMeetingTypes.find((t) => t.templateId === initialData.fileNoteTemplateId)
          ?? filteredMeetingTypes.find((t) => t.templateName === initialData.meetingType);
        setForm({
          ...makeInitialForm(),
          ...initialData,
          selectedClient: initialData.clientid || '',
          start: start.second(0).millisecond(0),
          end: end.second(0).millisecond(0),
          duration: Math.max(end.diff(start, 'minute'), 30),
          meetingTypeId: tpl?._id ?? '',
          selectedContacts: (Array.isArray(initialData.clients) ? initialData.clients : [])
            .map((c: any) => (typeof c === 'object' ? c._id : c)).filter(Boolean),
          selectedAdvisors: (Array.isArray(initialData.advisors) ? initialData.advisors : [])
            .map((a: any) => (typeof a === 'object' ? a._id : a)).filter(Boolean),
          fileNoteTemplateId: tpl?.templateId ?? initialData.fileNoteTemplateId ?? '',
          meetingCategory: (initialData.category as 'financial' | 'general') ?? 'financial',
          participants: Array.isArray(initialData.participants) ? initialData.participants.join(', ') : '',
        });
      } else {
        const advisor = filteredAdvisors.find(
          (a) => a.email?.toLowerCase() === login.data.email.toLowerCase(),
        );
        const primary = fetched.find((c) => c.isPrimary);
        const tpl = filteredMeetingTypes[0];
        setForm({
          ...makeInitialForm(),
          selectedClient: clientId || '',
          selectedAdvisors: advisor ? [advisor._id] : [],
          selectedContacts: primary ? [primary._id] : [],
          meetingTypeId: tpl?._id ?? '',
          fileNoteTemplateId: tpl?.templateId ?? '',
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialData, clientId, login?.data?.email, filteredMeetingTypes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name in errors) setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string | string[]>) => {
    const { name, value } = e.target;
    if (name === 'meetingTypeId') {
      if (value === 'new-template') { navigate('/admin?tab=templates'); return; }
      const tpl = filteredMeetingTypes.find((t) => t._id === value);
      if (tpl) setForm((prev) => ({ ...prev, meetingTypeId: tpl._id, meetingType: tpl.templateName, fileNoteTemplateId: tpl.templateId || '' }));
      setErrors((prev) => ({ ...prev, meetingType: false }));
    } else {
      setForm((prev) => ({ ...prev, [name!]: typeof value === 'string' ? value.split(',') : value as string[] }));
    }
  };

  const handleTimeChange = (_field: 'start' | 'end', newValue: dayjs.Dayjs | null) => {
    const newStart = newValue ?? dayjs().second(0).millisecond(0);
    setForm((prev) => ({
      ...prev,
      start: newStart.second(0).millisecond(0),
      end: newStart.add(prev.duration, 'minute').second(0).millisecond(0),
    }));
  };

  const handleDurationChange = (e: SelectChangeEvent<number>) => {
    const d = Number(e.target.value);
    setForm((prev) => ({ ...prev, duration: d, end: prev.start.add(d, 'minute') }));
  };

  const handleCalendarSelect = (date: Date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return;
    const newStart = dayjs(date).second(0).millisecond(0);
    setForm((prev) => ({ ...prev, start: newStart, end: newStart.add(prev.duration, 'minute') }));
  };

  const handleCalendarNavigate = (action: string, view: string, currentDate: Date) => {
    const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';
    let d = dayjs(currentDate);
    if (action === 'PREV') d = d.subtract(1, unit);
    else if (action === 'NEXT') d = d.add(1, unit);
    const newStart = d.hour(form.start.hour()).minute(form.start.minute()).second(0).millisecond(0);
    setForm((prev) => ({ ...prev, start: newStart, end: newStart.add(prev.duration, 'minute') }));
  };

  const validateForm = () => {
    const newErrors: FormErrors = {
      title: false,
      selectedContacts: form.meetingCategory === 'financial' && form.selectedContacts.length === 0,
      meetingType: false,
      selectedClient: context === 'global' && !form.selectedClient,
      calendlyEvent: schedulingMethod === 'calendly' && !selectedCalendlyUrl,
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((v) => !v);
  };

  const buildMeetingPayload = () => {
    const finalEnd = mode !== 'edit' ? form.start.add(form.duration, 'minute') : form.end;
    return {
      data: {
        title: form.title,
        description: form.description,
        category: form.meetingCategory,
        participants: form.participants.split(',').map((p) => p.trim()).filter(Boolean),
        clientid: form.meetingCategory === 'general' ? (form.selectedClient || null) : (form.selectedClient || clientId),
        clients: form.selectedContacts,
        advisors: form.selectedAdvisors.length > 0 ? form.selectedAdvisors : [login.data.id],
        meetingType: form.meetingType,
        meetingMedium: form.meetingMedium,
        start: { date: form.start.format('YYYY-MM-DD'), time: form.start.format('HH:mm') },
        end: { date: finalEnd.format('YYYY-MM-DD'), time: finalEnd.format('HH:mm') },
        utcStartTime: form.start.toISOString(),
        utcEndTime: finalEnd.toISOString(),
        timeZone: getBrowserTimeZone(),
        orgId: login.data.orgId,
        userId: login.data.id,
        fileNoteTemplateId: form.meetingCategory === 'general' ? null : form.fileNoteTemplateId,
      },
    };
  };

  const resetForm = () => {
    setForm(makeInitialForm());
    setErrors({ title: false, selectedContacts: false, meetingType: false, selectedClient: false, calendlyEvent: false });
  };

  return {
    form,
    setForm,
    errors,
    setErrors,
    filteredMeetingTypes,
    filteredAdvisors,
    handleChange,
    handleSelectChange,
    handleTimeChange,
    handleDurationChange,
    handleCalendarSelect,
    handleCalendarNavigate,
    validateForm,
    buildMeetingPayload,
    resetForm,
  };
};
